import React, { useState } from "react";

function Documentation() {
  const baseUrl = "http://localhost:3000";
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="mb-3">
      <h2 className="mb-0">Similarity Search </h2>
      <button className="btn btn-link ps-0 pt-0" type="button" onClick={toggleExpand}>
        <small>{isExpanded ? "Collapse" : "Read"} documentation</small>
      </button>
      <div className={`alert alert-info collapse ${isExpanded ? "show" : ""}`}>
        <p>Search by meaning -- find semantically similar content.</p>
        <p>
          This feature uses high-dimensional vectors, or embeddings, to compare the semantic content of texts by
          measuring the distance between these vectors. This allows you to identify content with similar meanings or
          topics, even if the exact wording is different. This flexibility is useful for finding requests, pitches,
          quotes, or media items that share a thematic connection.
        </p>
        <h4>How to Use</h4>
        <h6>Two modes: Qwery &amp; Advanced</h6>
        <ol>
          <li>
            <strong>Qwery:</strong> A simplified mode that tries to solve the basic problem for Media users and
            Promoters. Most folks will find this easy to use and understand.
          </li>
          <li>
            <strong>Advanced:</strong> Exposes all the parameters that the API supports.
          </li>
        </ol>
        <h6>Advanced mode</h6>
        <ol>
          <li>
            To search by text query, go to the &quot;Query Text&quot; tab and enter your query in the &quot;Query&quot;
            text area. The more text you provide, the better the results will be.
          </li>
          <li>
            To search by a specific model instance, go to the &quot;Search by Object&quot; tab and select the
            &quot;Query Model Type&quot; and enter the corresponding &quot;Query Model ID&quot;.
          </li>
          <li>
            You can narrow down the search results by selecting specific content types in the &quot;Search Model
            Types&quot; checkboxes.
          </li>
          <li>
            Adjust the other search parameters as needed:
            <ul>
              <li>
                &quot;Embedding Types&quot;: Choose the type of embeddings to search within. AggregatedEmbeddings only
                apply to PromoEntities and Profiles (both types of profiles) -- all of the text associated with the
                profile is grouped together and turned into embeddings as a unit. The idea is to search against the
                profile as a whole.
              </li>
              <li>&quot;Within Distance&quot;: Set a cutoff for the distance metric.</li>
              <li>&quot;Order By&quot;: Select the ordering for the search results.</li>
              <li>&quot;Distance Type&quot;: Choose the distance metric for comparing the embeddings.</li>
              <li>
                &quot;Model Timestamp Within Hours&quot;: Limit the search to models updated within the specified number
                of hours.
              </li>
              <li>&quot;Limit&quot;: Set the maximum number of results to return.</li>
              <li>&quot;Search Vector Column&quot;: Select the embedding column to perform the search on.</li>
            </ul>
          </li>
          <li>Click the &quot;Submit&quot; button to perform the search.</li>
        </ol>
        <h4>URL Parameters</h4>
        <p>
          You can also prefill the search parameters by including them in the URL when accessing the Similarity Search
          Console. Here are some examples:
        </p>
        <ul>
          <li>
            Query:
            <br />
            <code>{baseUrl}/admin/similarity_search_console?query=Test</code>
          </li>
          <li>
            Query Model Type:
            <br />
            <code>{baseUrl}/admin/similarity_search_console?query_model_type=Id::PromoEntity::Expert</code>
          </li>
          <li>
            Query Model Embedding Type:
            <br />
            <code>{baseUrl}/admin/similarity_search_console?query_model_embedding_type=MatchFeed::Embedding</code>
          </li>
          <li>
            Query Model ID:
            <br />
            <code>{baseUrl}/admin/similarity_search_console?query_model_id=123</code>
          </li>
          <li>
            Search Model Types:
            <br />
            <code>
              {baseUrl}
              /admin/similarity_search_console?search_model_types=Id::PromoEntity::Expert,Id::PromoEntity::Company
            </code>
          </li>
          <li>
            Embedding Types:
            <br />
            <code>{baseUrl}/admin/similarity_search_console?embedding_types=MatchFeed::Embedding</code>
          </li>
          <li>
            Within Distance:
            <br />
            <code>{baseUrl}/admin/similarity_search_console?within_distance=0.5</code>
          </li>
          <li>
            Order By:
            <br />
            <code>{baseUrl}/admin/similarity_search_console?order_by=model_timestamp_desc</code>
          </li>
          <li>
            Model Timestamp Within Hours:
            <br />
            <code>{baseUrl}/admin/similarity_search_console?model_timestamp_within_hours=24</code>
          </li>
          <li>
            Limit:
            <br />
            <code>{baseUrl}/admin/similarity_search_console?limit=50</code>
          </li>
          <li>
            Search Vector Column:
            <br />
            <code>{baseUrl}/admin/similarity_search_console?search_vector_column=text_embedding_ada_002_1536</code>
          </li>
          <li>
            Distance Type:
            <br />
            <code>{baseUrl}/admin/similarity_search_console?distance_type=euclidean</code>
          </li>
        </ul>
        <p>
          You can combine multiple parameters in a single URL to prefill multiple controls at once. For example:
          <br />
          <code>
            {baseUrl}
            /admin/similarity_search_console?query=Test&search_model_types=Id::PromoEntity::Expert,Id::PromoEntity::Company&limit=20
          </code>
        </p>
      </div>
    </div>
  );
}

export default Documentation;
